import {BehaviorSubject} from 'rxjs';
import {isEmpty, uniq, isArray, groupBy, orderBy, words} from 'lodash';
import SubscriptionsStore from './subscriptions';
import Api from '../api/Api';
import {normalizePagedResults} from '../helpers/normalizeResults';
import {formatGmailResult} from '../helpers/gmail';
import authentication from '../services/authentication';
import {removeStopWords} from '../helpers/various';
import PerformanceStore from './performance';
import axios from 'axios';

const emptyPlatform = { page: 0, lastPage: false, data: [] };
const typeFilters = ['email', 'message', 'file'];
const PAGE_SIZE = 5;

const sortResults = (results:any) => {
  const sort = SearchStore.sortValue$.getValue();
  let sorted;
  if (sort === 'date') {
    sorted = orderBy(results, (item) => {
      return new Date(item.date).getTime();
    }, ['desc'])
  } else {
    sorted = orderBy(results, 'rank', 'asc');
  }
  return sorted;
}

const SearchStore = {
  error$: new BehaviorSubject<null | any>(null),
  searchQuery$: new BehaviorSubject(''),
  highlights$: new BehaviorSubject<Array<string>>([]),
  searchId$: new BehaviorSubject(''),
  page$: new BehaviorSubject(0),
  searchSuggestions$: new BehaviorSubject<Array<any>>([]),
  platformFilter$: new BehaviorSubject<string>('all'),
  typeFilter$: new BehaviorSubject<string>(''),
  sortValue$: new BehaviorSubject(''),
  results$: new BehaviorSubject<null | any>(null),
  normalizedResults$: new BehaviorSubject<Array<any>>([]),
  displayResults$: new BehaviorSubject<Array<any>>([]),
  displayFilters$: new BehaviorSubject<any>({ platforms: [], types: typeFilters}),
  groupedData$: new BehaviorSubject<any>({ all: { ...emptyPlatform }}),
  loading$: new BehaviorSubject(false),
  pageLoading$: new BehaviorSubject<boolean>(false),
  hasMore$: new BehaviorSubject(true),
  cancelToken: null as any,
  cancelPageRequest() {
    if (this.cancelToken && this.pageLoading$.getValue()) {
      this.cancelToken.cancel('Cancel in-flight page request on platform filter')
      this.pageLoading$.next(false);
    }
  },
  async setQuery(query:string) {
    const currentQuery = this.searchQuery$.getValue();
    // don't do another search with same query
    // or if another search is in flight
    if (query === currentQuery || this.loading$.getValue()) {
      return;
    }
    this.searchQuery$.next(query);
    if (!query) {
      return;
    }
    try {
      this.loading$.next(true);
      PerformanceStore.reset();
      // reset filters and results on new search
      this.platformFilter$.next('all');
      this.typeFilter$.next('');
      this.displayFilters$.next({ platforms: [], types: typeFilters});
      this.results$.next([]);
      this.displayResults$.next([]);
      this.normalizedResults$.next([]);
      const sanitizedQuery = query.replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')
      this.highlights$.next(words(removeStopWords(sanitizedQuery)));
      //await SearchStore.search();
    } catch (err) {
      console.log('error', err);
    }
  },
  setSort(sort:string) {
    SearchStore.sortValue$.next(sort);
    let displayResults = this.displayResults$.getValue();
    this.displayResults$.next(sortResults(displayResults));
  },
  async setFilter(filter:string) {
    const platformFilter = this.platformFilter$.getValue();
    if (!filter || (platformFilter === filter && filter !== 'all')) {
      return;
    }
    const allNormalized = this.normalizedResults$.getValue();
    if (filter === 'all') {
      this.displayResults$.next(allNormalized);
      this.platformFilter$.next('all');
      this.typeFilter$.next('');
      this.page$.next(Math.ceil(allNormalized.length / PAGE_SIZE))
      this.hasMore$.next(true);
      this.nextPage();
      return;
    }
    const displayFilters = this.displayFilters$.getValue();

    //check for type filter
    // if type filter, set platform filter to ALL
    const isTypeFilter = displayFilters.types.indexOf(filter) > -1;
    if (isTypeFilter) {
      this.platformFilter$.next('');
      this.typeFilter$.next(filter);
      // apply type filters only on FE based on already given results
      const displayResults = allNormalized.filter((r:any) => r.filter === filter);
      this.displayResults$.next(sortResults(displayResults));
      // don't request more results
      this.hasMore$.next(false);
    } else {
      PerformanceStore.reset();
      this.cancelPageRequest();
      this.platformFilter$.next(filter);
      this.typeFilter$.next('');
      // reset page and request new one when new filter is applied
      const existingResults = allNormalized.filter((r:any) => r.platform === filter);
      this.page$.next(existingResults.length < PAGE_SIZE ? 0 : Math.floor(existingResults.length / PAGE_SIZE));
      this.displayResults$.next(sortResults(existingResults) || []);
      //this.results$.next([]);
      //this.normalizedResults$.next([]);
      this.hasMore$.next(true);
      await this.nextPage();
    }
  },
  async nextPage(page?:number) {
    if (!this.hasMore$.getValue() || this.pageLoading$.getValue()) {
      return;
    }
    this.pageLoading$.next(true);
    const currentPage = page || this.page$.getValue();
    const currentSearch = this.searchId$.getValue();
    const platformFilter = this.platformFilter$.getValue();
    const groupedData = this.groupedData$.getValue();
    // platform specific page
    const nextPage = currentPage + 1;
    if (groupedData[platformFilter]?.lastPage) {
      this.hasMore$.next(false);
      return;
    }
    this.page$.next(nextPage);
    try {
      const requestPerf = performance.now();
      this.cancelToken = axios.CancelToken.source();
      const { data } = await Api.pageSearch(nextPage, currentSearch, platformFilter, this.cancelToken);
      PerformanceStore.registerPageData({
        page: nextPage,
        request: performance.now() - requestPerf,
      });
      if (!data || !data.ok) {
        this.loading$.next(false);
        this.hasMore$.next(false);
        const resValue = this.results$.getValue();
        if (!resValue || (resValue && !resValue.length)) {
          this.error$.next({
            search: data.message,
          });
        }
        return;
      }
      if (!isArray(data.message)) {
        this.loading$.next(false);
        this.hasMore$.next(false);
        return;
      }
      if (data.last_page) {
        this.hasMore$.next(false);
      }
      const currentResults = this.results$.getValue() || [];
      // filter out unexpected platforms
      const filteredRes = data.message.filter((m:any) => {
        const existing = currentResults.some((r:any) => r.result_id === m.result_id);
        return !existing;
      });
      const parsePerf = performance.now();
      const normalizedFiltered = normalizePagedResults(filteredRes, SearchStore.highlights$.getValue());
      PerformanceStore.registerPageData({
        page: nextPage,
        parse: performance.now() - parsePerf,
      })
      const allResults = [
        ...currentResults,
        ...filteredRes,
      ]
      const allNormalized = [
        ...this.normalizedResults$.getValue(),
        ...normalizedFiltered,
      ]
      this.pageLoading$.next(false);
      this.normalizedResults$.next(allNormalized);
      this.results$.next(allResults);
      this.error$.next(null);

    } catch (err) {
      if (axios.isCancel(err)) {
        console.log(err.message);
        return;
      }
      console.log(err);
      this.error$.next({
        search: err,
      });
      this.pageLoading$.next(false);
    }
  },
  clearError() {
    this.error$.next(null);
  },
  async search() {
    const query = this.searchQuery$.getValue();
    this.loading$.next(true);
    this.results$.next(null);
    this.normalizedResults$.next([]);
    this.displayResults$.next([]);
    try {
      const resp = await Api.mqSearch(query);
      this.searchId$.next(resp.data.search_id);
      this.hasMore$.next(true);
      this.page$.next(0);
      this.error$.next(null);
      await this.nextPage();
      this.loading$.next(false);
    } catch (err) {
      this.loading$.next(false);
      this.error$.next({
        search: err,
      });
    }
  },
  async setSearchId(searchId:string) {
    if (!searchId) {
      return;
    }
    try {
      this.searchId$.next(searchId);
      this.hasMore$.next(true);
      this.page$.next(0);
      this.error$.next(null);
      await this.nextPage();
      this.loading$.next(false);
    } catch (err) {
      this.loading$.next(false);
      this.error$.next({
        search: err,
      });
    }
  },
  async fetchFullResult(item:any) {
    try {
      const fullItem = await Api.getFullGmail({
        msg_id: item.msg_id,
        userId: item.email,
        platform: 'gmail'
      });
      const { data } = fullItem;
      if (data && data.result) {
        let normalized = this.normalizedResults$.getValue();
        const results = this.results$.getValue();
        const highlights = results && results.highlight ? results.highlight : [];
        const newResult = formatGmailResult(data.result, highlights, true);
        const fullResult = {
          ...item,
          fullMessage: newResult && newResult.length ? newResult : 'No content available',
          fullFetched: true,
        };
        normalized = normalized.map((res) => {
          if (res.msg_id === item.msg_id) {
            return {
              ...res,
              fullMessage: newResult && newResult.length ? newResult : 'No content available',
              fullFetched: true,
            };
          }
          return res;
        });
        this.normalizedResults$.next(normalized);
        return fullResult;
      }
      return item;
    } catch (err) {
      console.log(' fetch message error', err);
      this.error$.next({
        fullResult: err,
      })
    }
  },
  async getSearchSuggestions(query:string) {
    if (!query) {
      this.searchSuggestions$.next([]);
      return;
    }
    try {
      const trovaId = authentication.getUserData().trova_id;
      let result:any = await Api.getSearchSuggestions(trovaId, query);
      let suggestions:any = [];

      if (result && result.data && result.data.suggestions) {
        suggestions = result.data.suggestions;
      }

      this.searchSuggestions$.next(suggestions);
    } catch (err) {
      console.log(err);
      this.error$.next({
        suggestions: err,
      });
    }
  },
  groupResults(data:any) {
    const normalized = normalizePagedResults(data, SearchStore.highlights$.getValue());
    const groups = groupBy(normalized, 'platform');
    const page = SearchStore.page$.getValue();
    let groupedData = this.groupedData$.getValue();
    groupedData.all = {
      ...groupedData.all,
      page,
      data: [
        ...groupedData.all.data,
        ...normalized
      ],
    }
    Object.keys(groups).forEach((k) => {
      groupedData[k] = groupedData[k] || { ...emptyPlatform };
      groupedData[k] = {
        ...groupedData[k],
        page,
        data: [
          ...groupedData[k].data,
          ...groups[k],
        ]
      }
    });
    return groupedData;
  }
};

// setup platform filters
SubscriptionsStore.normalized$.subscribe({
  next: (value) => {
    if (isEmpty(value)) {
      return;
    }
    const displayFilters = SearchStore.displayFilters$.getValue();
    const platforms = Object.keys(value).filter((pl:string) => pl !== 'teams');
    SearchStore.displayFilters$.next({
      ...displayFilters,
      platforms,
    })
  }
});

SearchStore.results$.subscribe({
  next: (value) => {
    if (isEmpty(value)) {
      return;
    }
    // update type filters based on current results
    const subscriptions = SubscriptionsStore.normalized$.getValue();
    const platforms = Object.keys(subscriptions).filter((pl:string) => pl !== 'teams');
    const types = uniq(value.map((res:any) => res.filter));
    SearchStore.displayFilters$.next({
      platforms,
      types,
    })
  }
})

SearchStore.normalizedResults$.subscribe({
  next: (value) => {
    if (!value.length) {
      SearchStore.displayResults$.next([]);
      return;
    }
    // set results for display based on selected filters
    const platformFilter = SearchStore.platformFilter$.getValue();
    const typeFilter = SearchStore.typeFilter$.getValue();
    let displayResults = value.filter((nr:any) => {
      if (platformFilter === 'all' && !typeFilter) {
        return true;
      }
      if (typeFilter) {
        return nr.filter === typeFilter;
      }
      return nr.platform === platformFilter;
    })
    SearchStore.displayResults$.next(sortResults(displayResults));
  }
});

SearchStore.displayResults$.subscribe({
  next: async (value) => {
    const hasMore = SearchStore.hasMore$.getValue();
    if (!value.length || !hasMore) {
      return;
    }
    if (value.length < PAGE_SIZE * 2) {
      // fetch another page
      await SearchStore.nextPage();
    }
  }
})

export default SearchStore;